import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import type { HowItWorksStep } from "../../types/how-it-works";
import "./HowItWorksSection.css";

interface HowItWorksSectionProps {
  steps: HowItWorksStep[];
}

const useReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const StepCard: React.FC<{
  step: HowItWorksStep;
  index: number;
  reducedMotion: boolean;
}> = ({ step, index, reducedMotion }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const { t } = useTranslation("marketing");

  const animationProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 30 } as const,
        animate: inView ? ({ opacity: 1, y: 0 } as const) : ({ opacity: 0, y: 30 } as const),
        transition: { duration: 0.5, delay: index * 0.15 },
      };

  return (
    <motion.div
      ref={ref}
      className="how-it-works__card"
      {...animationProps}
    >
      <div className="how-it-works__badge" aria-hidden="true">
        {String(step.stepNumber).padStart(2, "0")}
      </div>
      <div className="how-it-works__icon" aria-hidden="true">
        {step.icon}
      </div>
      <h3 className="how-it-works__card-title">{t(step.titleKey)}</h3>
      <p className="how-it-works__card-description">{t(step.descriptionKey)}</p>
    </motion.div>
  );
};

const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({ steps }) => {
  const { t } = useTranslation("marketing");
  const reducedMotion = useReducedMotion();
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  const headerAnimationProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 30 } as const,
        animate: headerInView ? ({ opacity: 1, y: 0 } as const) : ({ opacity: 0, y: 30 } as const),
        transition: { duration: 0.5 },
      };

  return (
    <section className="how-it-works" aria-label={t("landing.howItWorks.title")}>
      <div className="how-it-works__content">
        <motion.div
          ref={headerRef}
          className="how-it-works__header"
          {...headerAnimationProps}
        >
          <h2 className="how-it-works__title">{t("landing.howItWorks.title")}</h2>
          <p className="how-it-works__subtitle">{t("landing.howItWorks.subtitle")}</p>
        </motion.div>

        <div className="how-it-works__grid">
          {steps.map((step, index) => (
            <StepCard
              key={step.stepNumber}
              step={step}
              index={index}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
